/**
 * Touch Menu Handler for iPad/Tablet Navigation
 * Handles touch interactions on desktop menus for tablet devices
 * Prevents immediate link navigation, allows submenu opening on first tap
 *
 * @author    Bilal Harry Lindemann
 * @copyright Copyright (c) 2025 LindemannRock
 */

export function initTouchMenu() {
	// Only run on touch devices with larger screens (tablets)
	if (!('ontouchstart' in window) || window.innerWidth < 1024) {
		return;
	}

	// Track which menu items are currently open
	const openMenus = new Set<HTMLElement>();

	// Handle all parent links with dropdowns
	document.addEventListener('click', (e) => {
		const target = e.target as HTMLElement;
		const clickedElement = target.closest('a, button') as HTMLAnchorElement | HTMLButtonElement;

		if (!clickedElement) return;

		// Find the parent group container (any level)
		// Check for .group or any class starting with group/
		let parent: HTMLElement | null = null;

		const checkParent = clickedElement.parentElement;
		if (checkParent) {
			parent = checkParent.closest('[class*="group"]') as HTMLElement;
		}

		if (!parent) return;

		// Check if this parent has a dropdown
		const dropdown = parent.querySelector('.invisible');
		if (!dropdown) return; // No dropdown, let link work normally

		// Check if menu is already open
		if (openMenus.has(parent)) {
			// Second tap - allow navigation only for links, not buttons
			if (clickedElement.tagName === 'A') {
				openMenus.delete(parent);
				parent.classList.remove('touch-open');
				return; // Let the link work
			}
		}

		// First tap - prevent navigation and open menu
		e.preventDefault();
		e.stopPropagation();

		// Close other open menus at same level
		openMenus.forEach((openMenu) => {
			if (openMenu !== parent) {
				openMenu.classList.remove('touch-open');
				openMenus.delete(openMenu);
			}
		});

		// Open this menu
		parent.classList.add('touch-open');
		openMenus.add(parent);
	});

	// Close all menus when tapping outside
	document.addEventListener('click', (e) => {
		const target = e.target as HTMLElement;

		// If not clicking inside any menu
		if (!target.closest('[class*="group"]')) {
			openMenus.forEach((menu) => {
				menu.classList.remove('touch-open');
			});
			openMenus.clear();
		}
	});
}
